(function () {
  const body = document.body;
  const rootUrl = window.Shopify?.routes?.root || "/";
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const animationStyle = body.dataset.animationStyle || "fade-up";
  let headerTicking = false;
  let revealObserver = null;
  let buyBarObserver = null;
  let productLightbox = null;
  let productLightboxTrigger = null;

  const scopeMatches = (scope, selector) => {
    const matches = [];
    if (scope instanceof Element && scope.matches(selector)) matches.push(scope);
    scope.querySelectorAll?.(selector).forEach((item) => matches.push(item));
    return matches;
  };

  const setMenuState = (open) => {
    body.classList.toggle("nav-open", open);
    body.classList.toggle("menu-open", open);
    document.querySelectorAll("[data-menu-toggle]").forEach((toggle) => {
      if (toggle.matches("button")) toggle.setAttribute("aria-expanded", String(open));
    });
    document.querySelectorAll("[data-nav]").forEach((nav) => {
      nav.setAttribute("aria-hidden", String(!open));
    });
    if (open) {
      const header = document.querySelector("[data-site-header]");
      header?.classList.remove("minifly-header-hidden", "is-away-from-top");
      header?.classList.add("is-solid");
    }
  };

  const updateHeaderState = () => {
    const header = document.querySelector("[data-site-header]");
    if (!header) return;

    const currentY = Math.max(window.scrollY, 0);
    const awayFromTop = currentY > 48;

    header.classList.toggle("is-away-from-top", awayFromTop);
    header.classList.remove("is-transparent");
    header.classList.add("is-solid");
  };

  const initHeader = (scope) => {
    scopeMatches(scope, "[data-menu-toggle]").forEach((toggle) => {
      if (toggle.dataset.mfBound === "true") return;
      toggle.dataset.mfBound = "true";
      toggle.addEventListener("click", () => setMenuState(!body.classList.contains("nav-open")));
    });

    scopeMatches(scope, "[data-nav] a, .drawer-links a").forEach((link) => {
      if (link.dataset.mfBound === "true") return;
      link.dataset.mfBound = "true";
      link.addEventListener("click", () => setMenuState(false));
    });

    updateHeaderState();
  };

  const initGalleries = (scope) => {
    scopeMatches(scope, "[data-gallery]").forEach((gallery) => {
      if (gallery.dataset.mfBound === "true") return;
      gallery.dataset.mfBound = "true";
      const main = gallery.querySelector("[data-gallery-main]");

      gallery.addEventListener("click", (event) => {
        const thumb = event.target.closest("[data-gallery-image]");
        if (!thumb || !gallery.contains(thumb) || !main) return;
        const nextSrc = thumb.dataset.galleryImage;
        if (!nextSrc || thumb.classList.contains("is-active")) return;

        if (!reducedMotion) main.classList.add("is-switching");
        const settleImage = () => main.classList.remove("is-switching");
        main.addEventListener("load", settleImage, { once: true });
        main.removeAttribute("srcset");
        main.removeAttribute("sizes");
        main.src = nextSrc;
        main.alt = thumb.dataset.galleryAlt || "MiniFly product image";
        gallery.querySelectorAll("[data-gallery-image]").forEach((item) => {
          item.classList.remove("is-active");
          item.setAttribute("aria-current", "false");
        });
        thumb.classList.add("is-active");
        thumb.setAttribute("aria-current", "true");
        if (main.complete) window.requestAnimationFrame(settleImage);
      });
    });
  };

  const initHotspots = (scope) => {
    scopeMatches(scope, "[data-hotspot]").forEach((hotspot) => {
      if (hotspot.dataset.mfBound === "true") return;
      hotspot.dataset.mfBound = "true";
      hotspot.addEventListener("click", (event) => {
        event.stopPropagation();
        const section = hotspot.closest("[data-hotspot-section]");
        const willOpen = !hotspot.classList.contains("is-active");
        section?.querySelectorAll("[data-hotspot].is-active").forEach((item) => {
          item.classList.remove("is-active");
          item.setAttribute("aria-expanded", "false");
        });
        hotspot.classList.toggle("is-active", willOpen);
        hotspot.setAttribute("aria-expanded", String(willOpen));
      });
    });
  };

  const getRevealObserver = () => {
    if (revealObserver || !("IntersectionObserver" in window)) return revealObserver;
    revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-visible");
          revealObserver.unobserve(entry.target);
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -5%" }
    );
    return revealObserver;
  };

  const initReveal = (scope) => {
    const selector =
      ".proof-strip, .section, .product-hero, .section-tabs, .final-cta, .service-strip-section, .full-video-section, .hotspot-section";
    const editorMode = Boolean(window.Shopify?.designMode);

    scopeMatches(scope, selector).forEach((item) => {
      if (item.dataset.mfRevealBound === "true") {
        if (editorMode) item.classList.add("is-visible");
        return;
      }
      item.dataset.mfRevealBound = "true";

      if (reducedMotion || animationStyle === "none") {
        item.classList.add("is-visible");
        return;
      }

      item.classList.add("reveal-item");
      item
        .querySelectorAll(
          ":scope > .section-heading, :scope > .home-product-layout > *, :scope > .gallery, :scope > .buy-box, :scope > .split-section > *, :scope article, :scope .step-card"
        )
        .forEach((child, index) => {
        child.classList.add("reveal-child");
        child.style.setProperty("--reveal-order", String(Math.min(index, 8)));
        });

      if (editorMode) {
        item.classList.add("is-visible");
      } else {
        const observer = getRevealObserver();
        if (observer) observer.observe(item);
        else item.classList.add("is-visible");
      }
    });
  };

  const formatMoney = (cents, currency) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || "en-US", {
        style: "currency",
        currency: currency || window.Shopify?.currency?.active || "USD",
      }).format(Number(cents || 0) / 100);
    } catch (_error) {
      return `$${(Number(cents || 0) / 100).toFixed(2)}`;
    }
  };

  const initVariantSelectors = (scope) => {
    scopeMatches(scope, "form[action*='/cart/add'] select[name='id']").forEach((select) => {
      if (select.dataset.mfBound === "true") return;
      select.dataset.mfBound = "true";

      const updateVariant = () => {
        const option = select.selectedOptions[0];
        if (!option) return;
        const productScope = select.closest(".product-hero, .home-product-layout");
        if (option.dataset.price) {
          const formattedPrice = formatMoney(option.dataset.price);
          productScope?.querySelector("[data-product-price]")?.replaceChildren(formattedPrice);
          document.querySelector("[data-mobile-buy-price]")?.replaceChildren(formattedPrice);
        }
        const comparePrice = productScope?.querySelector("[data-product-compare-price]");
        if (comparePrice && option.dataset.comparePrice) {
          const hasComparePrice = Number(option.dataset.comparePrice) > Number(option.dataset.price || 0);
          comparePrice.replaceChildren(hasComparePrice ? formatMoney(option.dataset.comparePrice) : "");
          comparePrice.hidden = !hasComparePrice;
        }
        const available = option.dataset.available !== "false";
        select.closest("form")?.querySelectorAll("button[type='submit']").forEach((button) => {
          button.disabled = !available;
        });
        const mobileButton = document.querySelector("[data-mobile-buy-bar] button");
        if (mobileButton) mobileButton.disabled = !available;

        const urgency = productScope?.querySelector("[data-stock-urgency]");
        if (urgency) {
          const useInventory = urgency.dataset.useInventory === "true";
          const tracked = option.dataset.inventoryTracked === "true";
          const fallback = Math.max(0, Number(urgency.dataset.fallbackCount || 0));
          const capacity = Math.max(1, Number(urgency.dataset.stockCapacity || 1));
          const inventory = Math.max(0, Number(option.dataset.inventory || 0));
          const stockCount = available ? (useInventory && tracked ? inventory : fallback) : 0;
          const percent = stockCount > 0 ? Math.max(6, Math.min(100, (stockCount / capacity) * 100)) : 0;
          urgency.querySelector("[data-stock-count]")?.replaceChildren(String(stockCount));
          urgency.querySelector("[data-stock-progress]")?.style.setProperty("--stock-progress", `${percent}%`);
          urgency.querySelector("[data-stock-available-text]")?.toggleAttribute("hidden", stockCount <= 0);
          urgency.querySelector("[data-stock-soldout-text]")?.toggleAttribute("hidden", stockCount > 0);
          urgency.classList.toggle("is-sold-out", stockCount <= 0);
        }
      };

      select.addEventListener("change", updateVariant);
      updateVariant();
    });
  };

  const initStockUrgency = (scope) => {
    scopeMatches(scope, "[data-stock-urgency]").forEach((urgency) => {
      if (urgency.dataset.mfBound === "true") return;
      urgency.dataset.mfBound = "true";
      if (reducedMotion || !("IntersectionObserver" in window)) {
        urgency.classList.add("is-ready");
        return;
      }
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (!entry.isIntersecting) return;
          window.requestAnimationFrame(() => urgency.classList.add("is-ready"));
          observer.disconnect();
        },
        { threshold: 0.35, rootMargin: "0px 0px -8%" }
      );
      observer.observe(urgency);
    });
  };

  const initQuantitySteppers = (scope) => {
    scopeMatches(scope, "[data-quantity-stepper]").forEach((stepper) => {
      if (stepper.dataset.mfBound === "true") return;
      stepper.dataset.mfBound = "true";
      const input = stepper.querySelector("input[type='number']");
      if (!input) return;

      stepper.addEventListener("click", (event) => {
        const button = event.target.closest("button[data-quantity-change]");
        if (!button || !stepper.contains(button)) return;
        const step = Number(input.step || 1);
        const min = Number(input.min || 1);
        const max = input.max ? Number(input.max) : Number.POSITIVE_INFINITY;
        const next = Math.max(min, Math.min(max, Number(input.value || min) + Number(button.dataset.quantityChange) * step));
        input.value = String(next);
        input.dispatchEvent(new Event("change", { bubbles: true }));
      });
    });
  };

  const closeProductLightbox = () => {
    if (!productLightbox) return;
    productLightbox.classList.remove("is-open");
    productLightbox.setAttribute("aria-hidden", "true");
    body.classList.remove("product-lightbox-open");
    productLightboxTrigger?.focus?.();
    productLightboxTrigger = null;
  };

  const getProductLightbox = () => {
    if (productLightbox) return productLightbox;
    productLightbox = document.createElement("div");
    productLightbox.className = "product-lightbox";
    productLightbox.setAttribute("data-product-lightbox", "");
    productLightbox.setAttribute("role", "dialog");
    productLightbox.setAttribute("aria-modal", "true");
    productLightbox.setAttribute("aria-label", "Product image preview");
    productLightbox.setAttribute("aria-hidden", "true");

    const closeButton = document.createElement("button");
    closeButton.className = "product-lightbox-close";
    closeButton.type = "button";
    closeButton.setAttribute("aria-label", "Close product image preview");
    closeButton.innerHTML = "&times;";

    const image = document.createElement("img");
    image.alt = "";
    productLightbox.append(closeButton, image);
    body.append(productLightbox);

    closeButton.addEventListener("click", closeProductLightbox);
    productLightbox.addEventListener("click", (event) => {
      if (event.target === productLightbox) closeProductLightbox();
    });
    return productLightbox;
  };

  const initProductTools = (scope) => {
    scopeMatches(scope, "[data-product-wishlist]").forEach((button) => {
      if (button.dataset.mfBound === "true") return;
      button.dataset.mfBound = "true";
      const storageKey = `minifly:wishlist:${button.dataset.productKey || "product"}`;
      const setWishlistState = (active) => {
        button.classList.toggle("is-active", active);
        button.setAttribute("aria-pressed", String(active));
        button.setAttribute("aria-label", active ? "Remove from wishlist" : "Add to wishlist");
      };
      try {
        setWishlistState(window.localStorage.getItem(storageKey) === "true");
      } catch (_error) {
        setWishlistState(false);
      }
      button.addEventListener("click", () => {
        const active = button.getAttribute("aria-pressed") !== "true";
        setWishlistState(active);
        try {
          window.localStorage.setItem(storageKey, String(active));
        } catch (_error) {}
      });
    });

    scopeMatches(scope, "[data-gallery-expand]").forEach((button) => {
      if (button.dataset.mfBound === "true") return;
      button.dataset.mfBound = "true";
      button.addEventListener("click", () => {
        const productScope = button.closest(".home-product-layout, .product-hero");
        const source = productScope?.querySelector("[data-gallery-main]");
        if (!source) return;
        const lightbox = getProductLightbox();
        const image = lightbox.querySelector("img");
        image.src = source.currentSrc || source.src;
        image.alt = source.alt || "Product image";
        productLightboxTrigger = button;
        body.classList.add("product-lightbox-open");
        lightbox.setAttribute("aria-hidden", "false");
        window.requestAnimationFrame(() => lightbox.classList.add("is-open"));
        window.setTimeout(() => lightbox.querySelector(".product-lightbox-close")?.focus(), 80);
      });
    });
  };

  const initMobileBuyBar = () => {
    buyBarObserver?.disconnect();
    buyBarObserver = null;
    const mobileBuyBar = document.querySelector("[data-mobile-buy-bar]");
    const primaryProductForm = document.querySelector(".product-hero form[action*='/cart/add']");
    if (!mobileBuyBar || !primaryProductForm || !("IntersectionObserver" in window)) return;
    buyBarObserver = new IntersectionObserver(
      ([entry]) => mobileBuyBar.classList.toggle("is-active", !entry.isIntersecting && window.scrollY > 240),
      { threshold: 0.1 }
    );
    buyBarObserver.observe(primaryProductForm);
  };

  const initAutoplayMedia = (scope) => {
    scopeMatches(scope, "video[autoplay]").forEach((video) => {
      video.muted = true;
      video.playsInline = true;
      const playPromise = video.play();
      if (playPromise?.catch) playPromise.catch(() => {});
    });
  };

  const initScope = (scope) => {
    initHeader(scope);
    initGalleries(scope);
    initHotspots(scope);
    initReveal(scope);
    initVariantSelectors(scope);
    initStockUrgency(scope);
    initQuantitySteppers(scope);
    initProductTools(scope);
    initAutoplayMedia(scope);
    initMobileBuyBar();
  };

  const escapeHtml = (value) =>
    String(value ?? "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;");

  const cartDrawer = document.querySelector("[data-cart-drawer]");
  if (cartDrawer) {
    const cartContent = cartDrawer.querySelector("[data-cart-content]");
    const cartFooter = cartDrawer.querySelector("[data-cart-footer]");
    const cartLive = document.querySelector("[data-cart-live]");
    const threshold = Number(cartDrawer.dataset.freeShippingThreshold || 0);
    const shippingUnlocked = cartDrawer.dataset.shippingUnlocked || "You’ve unlocked free US shipping.";
    const shippingPrefix = cartDrawer.dataset.shippingPrefix || "Add";
    const shippingSuffix = cartDrawer.dataset.shippingSuffix || "more for free US shipping.";
    let lastCartTrigger = null;

    const fetchCart = async () => {
      const response = await fetch(`${rootUrl}cart.js`, { headers: { Accept: "application/json" } });
      if (!response.ok) throw new Error("Unable to load cart");
      return response.json();
    };

    const updateShipping = (cart) => {
      const remaining = Math.max(threshold - cart.total_price, 0);
      const percent = threshold > 0 ? Math.min((cart.total_price / threshold) * 100, 100) : 100;
      const message = document.querySelector("[data-cart-shipping-message]");
      const progress = document.querySelector("[data-cart-shipping-progress]");
      if (message) {
        message.textContent =
          remaining <= 0 ? shippingUnlocked : `${shippingPrefix} ${formatMoney(remaining, cart.currency)} ${shippingSuffix}`;
      }
      if (progress) progress.style.width = `${percent}%`;
    };

    const renderCart = (cart) => {
      document.querySelectorAll("[data-cart-count]").forEach((count) => {
        count.textContent = cart.item_count;
      });
      const headingCount = cartDrawer.querySelector("[data-cart-heading-count]");
      if (headingCount) headingCount.textContent = `(${cart.item_count})`;
      const subtotal = cartDrawer.querySelector("[data-cart-subtotal]");
      if (subtotal) subtotal.textContent = formatMoney(cart.total_price, cart.currency);
      updateShipping(cart);

      if (!cartContent) return;
      if (!cart.item_count) {
        cartContent.innerHTML = `
          <div class="cart-drawer-empty">
            <h3>${escapeHtml(cartDrawer.dataset.emptyHeading || "Your cart is ready for an adventure.")}</h3>
            <p>${escapeHtml(cartDrawer.dataset.emptyText || "Add a MiniFly set and keep checkout quick and secure.")}</p>
            <a class="button button-primary" href="${escapeHtml(cartDrawer.dataset.shopUrl || `${rootUrl}collections/all`)}">${escapeHtml(cartDrawer.dataset.shopLabel || "Shop MiniFly")}</a>
          </div>`;
        if (cartFooter) cartFooter.hidden = true;
        return;
      }

      cartContent.innerHTML = `<div class="cart-drawer-lines">${cart.items
        .map((item) => {
          const imageUrl = item.image ? `${item.image}${item.image.includes("?") ? "&" : "?"}width=220` : "";
          return `
            <article class="cart-drawer-line" data-cart-key="${escapeHtml(item.key)}">
              <a href="${escapeHtml(item.url)}" class="cart-drawer-image">
                ${imageUrl ? `<img src="${escapeHtml(imageUrl)}" alt="${escapeHtml(item.product_title)}" width="220" height="268">` : ""}
              </a>
              <div>
                <a href="${escapeHtml(item.url)}"><strong>${escapeHtml(item.product_title)}</strong></a>
                ${item.variant_title && item.variant_title !== "Default Title" ? `<p>${escapeHtml(item.variant_title)}</p>` : ""}
                <div class="cart-line-price">
                  ${item.original_line_price > item.final_line_price ? `<s>${formatMoney(item.original_line_price, cart.currency)}</s>` : ""}
                  <span>${formatMoney(item.final_line_price, cart.currency)}</span>
                </div>
                ${Array.isArray(item.discounts) && item.discounts.length ? `<div class="cart-line-discounts">${item.discounts.map((discount) => `<span>${escapeHtml(discount.title || "Discount")} -${formatMoney(discount.amount, cart.currency)}</span>`).join("")}</div>` : ""}
                <div class="cart-quantity" aria-label="Quantity selector">
                  <button type="button" data-cart-quantity="${Math.max(item.quantity - 1, 0)}" aria-label="Decrease quantity">−</button>
                  <b>${item.quantity}</b>
                  <button type="button" data-cart-quantity="${item.quantity + 1}" aria-label="Increase quantity">+</button>
                </div>
              </div>
              <button class="cart-remove" type="button" data-cart-quantity="0" aria-label="Remove ${escapeHtml(item.product_title)}">Remove</button>
            </article>`;
        })
        .join("")}</div>`;
      if (cartFooter) cartFooter.hidden = false;
    };

    const openCart = (trigger) => {
      lastCartTrigger = trigger || document.activeElement;
      setMenuState(false);
      body.classList.add("cart-open");
      cartDrawer.setAttribute("aria-hidden", "false");
      window.setTimeout(() => cartDrawer.querySelector("[data-cart-close]")?.focus(), 80);
    };

    const closeCart = () => {
      body.classList.remove("cart-open");
      cartDrawer.setAttribute("aria-hidden", "true");
      lastCartTrigger?.focus?.();
    };

    document.addEventListener("click", async (event) => {
      const cartTrigger = event.target.closest("[data-cart-open]");
      if (cartTrigger) {
        event.preventDefault();
        try {
          renderCart(await fetchCart());
          openCart(cartTrigger);
        } catch (_error) {
          window.location.href = cartTrigger.href || `${rootUrl}cart`;
        }
        return;
      }

      if (event.target.closest("[data-cart-close]")) {
        closeCart();
        return;
      }

      const quantityButton = event.target.closest("[data-cart-quantity]");
      if (!quantityButton || !cartDrawer.contains(quantityButton)) return;
      const line = quantityButton.closest("[data-cart-key]");
      if (!line) return;
      quantityButton.disabled = true;
      try {
        const response = await fetch(`${rootUrl}cart/change.js`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ id: line.dataset.cartKey, quantity: Number(quantityButton.dataset.cartQuantity) }),
        });
        if (!response.ok) throw new Error("Unable to update cart");
        renderCart(await response.json());
        if (cartLive) cartLive.textContent = "Cart updated.";
      } catch (error) {
        if (cartLive) cartLive.textContent = error.message;
        quantityButton.disabled = false;
      }
    });

    document.addEventListener("submit", async (event) => {
      const form = event.target.closest("form[action*='/cart/add']");
      if (!form) return;
      const submitter = event.submitter;
      if (submitter?.closest(".shopify-payment-button") || submitter?.classList.contains("shopify-payment-button__button")) return;

      event.preventDefault();
      submitter?.setAttribute("aria-busy", "true");
      if (submitter) submitter.disabled = true;
      try {
        const response = await fetch(`${rootUrl}cart/add.js`, {
          method: "POST",
          headers: { Accept: "application/json" },
          body: new FormData(form),
        });
        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.description || "Unable to add this item");
        }
        const cart = await fetchCart();
        renderCart(cart);
        openCart(submitter);
        if (cartLive) cartLive.textContent = "Item added to cart.";
      } catch (error) {
        if (cartLive) cartLive.textContent = error.message;
      } finally {
        submitter?.removeAttribute("aria-busy");
        if (submitter) submitter.disabled = false;
      }
    });

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && body.classList.contains("cart-open")) closeCart();
      if (event.key !== "Tab" || !body.classList.contains("cart-open")) return;
      const focusable = [...cartDrawer.querySelectorAll("a[href], button:not([disabled]), input, select, textarea")].filter(
        (element) => !element.hidden && element.offsetParent !== null
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  }

  document.addEventListener("click", (event) => {
    if (event.target.closest("[data-hotspot]")) return;
    document.querySelectorAll("[data-hotspot].is-active").forEach((item) => {
      item.classList.remove("is-active");
      item.setAttribute("aria-expanded", "false");
    });
  });

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && body.classList.contains("nav-open")) setMenuState(false);
    if (event.key === "Escape" && body.classList.contains("product-lightbox-open")) closeProductLightbox();
  });

  window.addEventListener(
    "scroll",
    () => {
      if (headerTicking) return;
      headerTicking = true;
      window.requestAnimationFrame(() => {
        updateHeaderState();
        headerTicking = false;
      });
    },
    { passive: true }
  );

  document.addEventListener("shopify:section:load", (event) => {
    initScope(event.target);
    window.requestAnimationFrame(() => {
      scopeMatches(event.target, ".reveal-item").forEach((item) => item.classList.add("is-visible"));
      updateHeaderState();
    });
  });

  document.addEventListener("shopify:section:select", (event) => {
    scopeMatches(event.target, ".reveal-item").forEach((item) => item.classList.add("is-visible"));
  });

  document.addEventListener("shopify:block:select", (event) => {
    event.target.closest?.(".reveal-item")?.classList.add("is-visible");
  });

  initScope(document);
})();

/* MiniFly mobile bottom-bar coordination — 2026-08-01 */
(function () {
  if (window.__mfBottomBarsReady) return;
  window.__mfBottomBarsReady = true;

  const root = document.documentElement;
  const syncBottomBars = function () {
    const nav = document.querySelector('.mobile-bottom-nav');
    const buyBar = document.querySelector('[data-mobile-buy-bar]');

    if (window.innerWidth > 767 || !buyBar) {
      root.style.removeProperty('--mf-mobile-nav-offset');
      root.style.removeProperty('--mf-mobile-buy-height');
      return;
    }

    const navVisible = nav && getComputedStyle(nav).display !== 'none';
    const navOffset = navVisible
      ? Math.max(0, window.innerHeight - nav.getBoundingClientRect().top)
      : 0;
    const buyHeight = Math.max(0, buyBar.getBoundingClientRect().height);

    root.style.setProperty('--mf-mobile-nav-offset', navOffset + 'px');
    root.style.setProperty('--mf-mobile-buy-height', buyHeight + 'px');
  };

  const scheduleSync = function () {
    window.requestAnimationFrame(syncBottomBars);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleSync, { once: true });
  } else {
    scheduleSync();
  }

  window.addEventListener('resize', scheduleSync, { passive: true });
  window.addEventListener('orientationchange', scheduleSync, { passive: true });
  window.visualViewport?.addEventListener('resize', scheduleSync, { passive: true });
  window.visualViewport?.addEventListener('scroll', scheduleSync, { passive: true });
  document.addEventListener('shopify:section:load', scheduleSync);

  if ('ResizeObserver' in window) {
    new ResizeObserver(scheduleSync).observe(document.documentElement);
  }
})();

/* MiniFly JS cache refresh 2026-08-01 v2 */

/* MINIFLY SECTION TABS V4 — fixed scrollspy navigation */
(() => {
  const NAV_SELECTOR = '#MainContent nav.section-tabs';
  const HEADER_SELECTORS = [
    '.shopify-section-group-header-group',
    'header[role="banner"]',
    'sticky-header',
    'header-component',
    '.header-wrapper'
  ].join(',');

  const controllers = window.__mfTabsControllers || new WeakMap();
  window.__mfTabsControllers = controllers;

  const initMiniFlyTabs = (nav) => {
    if (!nav || nav.dataset.mfStickyTabsInitialized === 'true') return;

    const directLinks = [...nav.children].filter((element) =>
      element.matches('a[href^="#"]')
    );
    if (!directLinks.length) return;

    const pairs = directLinks
      .map((link) => ({
        link,
        target: document.getElementById(decodeURIComponent(link.hash.slice(1)))
      }))
      .filter(({ target }) => target);
    if (!pairs.length) return;

    const previousController = controllers.get(nav);
    if (previousController) previousController.abort();
    const controller = new AbortController();
    const { signal } = controller;
    controllers.set(nav, controller);

    const shell = document.createElement('div');
    shell.className = 'mf-tabs-shell';
    nav.parentNode.insertBefore(shell, nav);
    shell.appendChild(nav);

    const scroller = document.createElement('div');
    scroller.className = 'section-tabs__scroller';
    scroller.setAttribute('data-mf-tabs-scroller', '');

    const indicator = document.createElement('span');
    indicator.className = 'section-tabs__indicator';
    indicator.setAttribute('aria-hidden', 'true');
    scroller.appendChild(indicator);

    pairs.forEach(({ link }) => scroller.appendChild(link));
    nav.insertBefore(scroller, nav.firstChild);

    const buyLink = document.createElement('a');
    buyLink.href = '#buy';
    buyLink.className = 'section-tabs__buy';
    buyLink.textContent = '↑ Buy';
    buyLink.setAttribute('aria-label', 'Back to purchase options');
    nav.appendChild(buyLink);

    nav.classList.add('mf-sticky-tabs');
    nav.dataset.mfStickyTabsInitialized = 'true';
    if (!nav.hasAttribute('aria-label')) {
      nav.setAttribute('aria-label', 'Product section navigation');
    }

    const links = pairs.map(({ link }) => link);
    const scrollPairs = [...pairs].sort((first, second) => {
      if (first.target === second.target) return 0;
      return first.target.compareDocumentPosition(second.target) &
        Node.DOCUMENT_POSITION_FOLLOWING
        ? -1
        : 1;
    });
    const firstTarget = scrollPairs[0].target;
    const lastTarget = scrollPairs[scrollPairs.length - 1].target;
    const endBoundary =
      lastTarget.closest('.shopify-section, [id^="shopify-section-"]') ||
      lastTarget;
    const mobile = matchMedia('(max-width: 767px)');
    const hoverCapable = matchMedia('(hover: hover)');
    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');

    pairs.forEach(({ target }) => {
      target.style.scrollMarginTop =
        'calc(var(--mf-tabs-header-offset, 0px) + var(--mf-tabs-height, 64px) + 16px)';
    });
    const buyTarget =
      document.querySelector('#buy .buy-box') || document.getElementById('buy');
    if (buyTarget) {
      buyTarget.style.scrollMarginTop =
        'calc(var(--mf-tabs-header-offset, 0px) + 16px)';
    }

    let activeLink = pairs[0].link;
    let ticking = false;

    const getHeaderOffset = () =>
      [...document.querySelectorAll(HEADER_SELECTORS)]
        .map((element) => ({
          rect: element.getBoundingClientRect(),
          position: getComputedStyle(element).position
        }))
        .filter(
          ({ rect, position }) =>
            (position === 'fixed' || position === 'sticky') && rect.bottom > 0
        )
        .sort((first, second) => first.rect.top - second.rect.top)
        .reduce(
          (bottom, { rect }) =>
            rect.top <= bottom + 1 ? Math.max(bottom, rect.bottom) : bottom,
          0
        );

    const paintIndicator = (link = activeLink) => {
      if (!link) return;
      scroller.style.setProperty('--mf-indicator-x', `${link.offsetLeft}px`);
      scroller.style.setProperty(
        '--mf-indicator-width',
        `${link.offsetWidth}px`
      );
    };

    const centerMobileLink = (link) => {
      if (!mobile.matches || !link) return;
      const desiredLeft =
        link.offsetLeft - (scroller.clientWidth - link.offsetWidth) / 2;
      scroller.scrollTo({
        left: Math.max(0, desiredLeft),
        behavior: reducedMotion.matches ? 'auto' : 'smooth'
      });
    };

    const setActive = (link, center = false) => {
      if (!link) return;
      links.forEach((item) => {
        const selected = item === link;
        item.classList.toggle('is-active', selected);
        if (selected) item.setAttribute('aria-current', 'location');
        else item.removeAttribute('aria-current');
      });

      const changed = activeLink !== link;
      activeLink = link;
      paintIndicator(link);
      if (center && changed) centerMobileLink(link);
    };

    const update = () => {
      const headerOffset = Math.ceil(getHeaderOffset());
      document.documentElement.style.setProperty(
        '--mf-tabs-header-offset',
        `${headerOffset}px`
      );

      const measuredHeight = Math.ceil(nav.getBoundingClientRect().height || 64);
      document.documentElement.style.setProperty(
        '--mf-tabs-height',
        `${measuredHeight}px`
      );
      shell.style.setProperty('--mf-tabs-height', `${measuredHeight}px`);

      const shellTop = shell.getBoundingClientRect().top;
      const shouldStick =
        shellTop <= headerOffset &&
        endBoundary.getBoundingClientRect().bottom >
          headerOffset + measuredHeight + 16;
      const stickyChanged = nav.classList.contains('is-sticky') !== shouldStick;

      shell.classList.toggle('is-sticky', shouldStick);
      nav.classList.toggle('is-sticky', shouldStick);

      const activationLine =
        headerOffset + (shouldStick ? measuredHeight : 0) + 24;
      let nextLink = scrollPairs[0].link;

      if (firstTarget.getBoundingClientRect().top <= activationLine) {
        for (const pair of scrollPairs) {
          if (pair.target.getBoundingClientRect().top <= activationLine) {
            nextLink = pair.link;
          } else {
            break;
          }
        }
      }

      const activeChanged = activeLink !== nextLink;
      setActive(nextLink, shouldStick && (stickyChanged || activeChanged));
      if (stickyChanged) {
        requestAnimationFrame(() => {
          paintIndicator(activeLink);
          if (shouldStick) centerMobileLink(activeLink);
        });
      }
    };

    const schedule = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        update();
      });
    };

    links.forEach((link) => {
      link.addEventListener(
        'pointerenter',
        () => {
          if (hoverCapable.matches) paintIndicator(link);
        },
        { signal }
      );
      link.addEventListener('pointerleave', () => paintIndicator(activeLink), {
        signal
      });
      link.addEventListener('focus', () => paintIndicator(link), { signal });
      link.addEventListener('blur', () => paintIndicator(activeLink), { signal });
      link.addEventListener(
        'click',
        (event) => {
          const pair = pairs.find((item) => item.link === link);
          if (!pair) return;

          event.preventDefault();
          const offset =
            Math.ceil(getHeaderOffset()) +
            Math.ceil(nav.getBoundingClientRect().height) +
            16;
          const top =
            pair.target.getBoundingClientRect().top + window.scrollY - offset;
          const historyMethod =
            window.location.hash === link.hash ? 'replaceState' : 'pushState';

          window.history[historyMethod](window.history.state, '', link.hash);
          window.scrollTo({
            top: Math.max(0, top),
            behavior: reducedMotion.matches ? 'auto' : 'smooth'
          });
          setActive(link, true);
        },
        { signal }
      );
    });

    buyLink.addEventListener(
      'click',
      (event) => {
        if (!buyTarget) return;

        event.preventDefault();
        const top =
          buyTarget.getBoundingClientRect().top +
          window.scrollY -
          (Math.ceil(getHeaderOffset()) + 16);
        const historyMethod =
          window.location.hash === '#buy' ? 'replaceState' : 'pushState';

        window.history[historyMethod](window.history.state, '', '#buy');
        window.scrollTo({
          top: Math.max(0, top),
          behavior: reducedMotion.matches ? 'auto' : 'smooth'
        });
      },
      { signal }
    );

    const syncFromHash = () => {
      requestAnimationFrame(() => {
        if (window.location.hash === '#buy' && buyTarget) {
          const top =
            buyTarget.getBoundingClientRect().top +
            window.scrollY -
            (Math.ceil(getHeaderOffset()) + 16);
          window.scrollTo({ top: Math.max(0, top), behavior: 'auto' });
          return;
        }

        const pair = pairs.find(({ link }) => link.hash === window.location.hash);
        if (!pair) return;
        const offset =
          Math.ceil(getHeaderOffset()) +
          Math.ceil(nav.getBoundingClientRect().height) +
          16;
        const top =
          pair.target.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top: Math.max(0, top), behavior: 'auto' });
        setActive(pair.link, true);
      });
    };

    const handleResize = () => {
      schedule();
      requestAnimationFrame(() => {
        if (nav.classList.contains('is-sticky')) centerMobileLink(activeLink);
      });
    };

    window.addEventListener('scroll', schedule, { passive: true, signal });
    window.addEventListener('resize', handleResize, { passive: true, signal });
    window.addEventListener('pageshow', schedule, { passive: true, signal });
    window.addEventListener('hashchange', syncFromHash, { signal });
    window.addEventListener('popstate', syncFromHash, { signal });

    if ('ResizeObserver' in window) {
      const resizeObserver = new ResizeObserver(schedule);
      resizeObserver.observe(nav);
      resizeObserver.observe(scroller);
      signal.addEventListener('abort', () => resizeObserver.disconnect(), {
        once: true
      });
    }

    nav.classList.add('is-enhanced');
    update();
    requestAnimationFrame(() => paintIndicator(activeLink));
  };

  const initAllMiniFlyTabs = (root = document) => {
    root.querySelectorAll(NAV_SELECTOR).forEach(initMiniFlyTabs);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAllMiniFlyTabs(), {
      once: true
    });
  } else {
    initAllMiniFlyTabs();
  }

  if (!window.__mfTabsSectionLoadListenerInstalled) {
    document.addEventListener('shopify:section:load', (event) => {
      initAllMiniFlyTabs(event.target);
    });
    document.addEventListener('shopify:section:unload', (event) => {
      event.target.querySelectorAll(NAV_SELECTOR).forEach((nav) => {
        controllers.get(nav)?.abort();
        controllers.delete(nav);
      });
    });
    window.__mfTabsSectionLoadListenerInstalled = true;
  }
})();
